import React, { useCallback, useEffect, useRef, useState } from 'react';
import { teacherApi } from '../services/api';
import FilterPills from './FilterPills';
import { AutoMark } from './GoalMarks';
import { goalAuthorBadge } from '../utils/goalAuthor';
import {
  GOAL_ACTIVE_EMPTY, GOAL_ARCHIVE_EMPTY, GOAL_ARCHIVE_RANGES, GOAL_PHASE_STYLE, goalCountsLms, goalHasStats, goalInRange, goalListPlace, goalPhase,
  type GoalArchiveRange,
} from '../utils/goalCard';
import { goalHeadline, goalScopeLine } from '../utils/goalLabel';
import { goalPace, paceLine, paceOrder, PACE_STYLE } from '../utils/goalPace';
import { fmtTimeRange, goalPeriodRange } from '../utils/goalWindow';
import { goalSpotlightStyle, useGoalSpotlight } from '../utils/useGoalSpotlight';

/**
 * Teacher → one student's Hedef Görünümü.
 *
 * Aktif: what is still to do, riskiest first, each running goal with its pace
 * (geride / yolunda / önde) and what is left. Arşiv: what ended or was
 * completed, narrowed to a range, with the hit rate on top. A strip above the
 * list sums the tab up. Lists come from the same API the student's own Hedefler
 * tabs read, so the counting is theirs.
 */

const PERIOD_LABEL: Record<string, string> = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };
const F = { fontFamily: 'Inter' } as const;

export interface TeacherGoalRow {
  id: number | string | bigint;
  target_value?: number;
  achieved_value?: number;
  correct_count?: number;
  wrong_count?: number;
  accuracy_rate?: number | string;
  start_time?: string | null;
  end_time?: string | null;
  goal_date?: string | null;
  status?: string;
  is_completed?: boolean;
  is_archived?: boolean;
  is_upcoming?: boolean;
  created_by_type?: string;
  created_by_name?: string | null;
  title?: string | null;
  tracking_mode?: string | null;
  auto_tracked?: boolean;
  period_type?: string;
  period_start?: string | null;
  period_end?: string | null;
  manual_solved?: number | null;
  goal_definitions?: { subjects?: { name?: string } | null } | null;
  subject_units?: { title?: string; subjects?: { name?: string } };
}

type Tab = 'aktif' | 'arsiv';
type Archive = { range: GoalArchiveRange | null; items: TeacherGoalRow[]; page: number; hasMore: boolean; loading: boolean };

interface Props {
  studentUuid: string;
  /** Bumped by the page after a change, so the lists are read again. */
  version: number;
  todayIso: string;
  /** A tap in Program Görünümü: show this goal, on the tab that lists it, lit. */
  focus: { goal: TeacherGoalRow; n: number } | null;
  tickLocked: (g: TeacherGoalRow) => boolean;
  tickPending: (g: TeacherGoalRow) => boolean;
  onTick: (g: TeacherGoalRow) => void;
  onDelete: (goalId: string) => void;
}

const Stat: React.FC<{ value: React.ReactNode; label: string; tone?: string }> = ({ value, label, tone = '#2b2b2b' }) => (
  <div className="flex-1 min-w-[96px] bg-white rounded-[16px] px-4 py-3" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)' }}>
    <p className="text-[22px] xl:text-[26px] font-bold leading-none tabular-nums" style={{ ...F, color: tone }}>{value}</p>
    <p className="text-[12px] text-gray-500 mt-1.5" style={F}>{label}</p>
  </div>
);

const StudentGoalList: React.FC<Props> = ({ studentUuid, version, todayIso, focus, tickLocked, tickPending, onTick, onDelete }) => {
  const [tab, setTab] = useState<Tab>('aktif');
  const [range, setRange] = useState<GoalArchiveRange>('week');
  // Aktif lists everything still to do; the pills narrow it to what falls today / this week / this month.
  const [activeRange, setActiveRange] = useState<GoalArchiveRange>('all');
  const [active, setActive] = useState<{ items: TeacherGoalRow[]; loading: boolean }>({ items: [], loading: true });
  const [archive, setArchive] = useState<Archive>({ range: null, items: [], page: 0, hasMore: false, loading: false });
  const archiveReq = useRef(0);
  const archiveLoaded = useRef(false);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const loadActive = useCallback(() => {
    setActive((a) => ({ ...a, loading: true }));
    teacherApi.studentGoals(studentUuid)
      .then((r) => setActive({ items: (r.data ?? []) as TeacherGoalRow[], loading: false }))
      .catch(() => setActive({ items: [], loading: false }));
  }, [studentUuid]);

  const loadArchive = useCallback(async (r: GoalArchiveRange, page: number) => {
    const req = ++archiveReq.current;
    archiveLoaded.current = true;
    setArchive((a) => ({ ...a, range: r, loading: true, ...(page === 1 && a.range !== r ? { items: [], hasMore: false } : {}) }));
    try {
      const res = await teacherApi.studentGoalArchive(studentUuid, r, page);
      if (req !== archiveReq.current) return;
      setArchive((a) => ({
        range: r,
        items: page === 1 ? (res.data.items as TeacherGoalRow[]) : [...a.items, ...(res.data.items as TeacherGoalRow[])],
        page,
        hasMore: !!res.data.has_more,
        loading: false,
      }));
    } catch {
      if (req === archiveReq.current) setArchive((a) => ({ ...a, loading: false }));
    }
  }, [studentUuid]);

  // Another student, or a change made on the page: read the lists again.
  useEffect(() => {
    loadActive();
    if (archiveLoaded.current) loadArchive(rangeRef.current, 1);
  }, [studentUuid, version, loadActive, loadArchive]);
  useEffect(() => { if (tab === 'arsiv') loadArchive(range, 1); }, [tab, range, loadArchive]);

  const listReady = tab === 'aktif' ? !active.loading : archive.range === range && !(archive.loading && archive.items.length === 0);
  const spotlight = useGoalSpotlight(listReady);
  const { request: spotRequest, pending: spotPending, cancel: spotCancel } = spotlight;
  const activeShown = active.items.filter((g) => goalInRange(g, activeRange, todayIso));

  useEffect(() => {
    if (!focus) return;
    const place = goalListPlace({ ...focus.goal, is_archived: !!focus.goal.is_archived }, todayIso);
    setTab(place.tab);
    if (place.tab === 'arsiv') setRange(place.range); else setActiveRange('all');
    spotRequest(focus.goal.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);
  // Not on the list yet: an older Arşiv page may hold it; otherwise let it go.
  useEffect(() => {
    if (!spotPending || !listReady) return;
    const list = tab === 'arsiv' ? archive.items : activeShown;
    if (list.some((x) => String(x.id) === spotPending)) return;
    if (tab === 'arsiv' && archive.loading) return;
    if (tab === 'arsiv' && archive.hasMore && archive.page < 10) loadArchive(range, archive.page + 1);
    else spotCancel();
  }, [spotPending, spotCancel, listReady, tab, archive, activeShown, range, loadArchive]);

  // ── Summary strip ──
  const paced = activeShown.map((g) => ({ g, pace: goalPace(g, todayIso) }));
  const sortedActive = [...paced].sort((a, b) => paceOrder(a.pace) - paceOrder(b.pace));
  const sum = (xs: TeacherGoalRow[], k: 'correct_count' | 'wrong_count' | 'achieved_value' | 'target_value') => xs.reduce((n, g) => n + (g[k] ?? 0), 0);
  const accuracy = (xs: TeacherGoalRow[]) => {
    const c = sum(xs, 'correct_count'), w = sum(xs, 'wrong_count');
    return c + w > 0 ? `%${Math.round((c / (c + w)) * 100)}` : '—';
  };
  const behind = paced.filter((p) => p.pace.state === 'behind').length;
  const done = archive.items.filter((g) => goalPhase(g) === 'done').length;
  const missed = archive.items.filter((g) => goalPhase(g) === 'missed').length;
  const rate = done + missed > 0 ? Math.round((done / (done + missed)) * 100) : null;

  const shown: Array<{ g: TeacherGoalRow; pace: ReturnType<typeof goalPace> }> = tab === 'arsiv'
    ? archive.items.map((g) => ({ g, pace: goalPace(g, todayIso) }))
    : sortedActive;
  const busy = tab === 'arsiv' ? archive.loading && archive.items.length === 0 : active.loading && active.items.length === 0;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterPills<Tab>
          tone="blue"
          value={tab}
          onChange={setTab}
          options={[{ value: 'aktif', label: 'Aktif Hedefler' }, { value: 'arsiv', label: 'Arşiv' }]}
        />
      </div>

      <div role="radiogroup" aria-label={tab === 'arsiv' ? 'Arşiv aralığı' : 'Aktif hedef aralığı'} className="flex items-center gap-1.5 mb-3 flex-wrap">
        {GOAL_ARCHIVE_RANGES.map((r) => {
          const picked = (tab === 'arsiv' ? range : activeRange) === r.value;
          return (
            <button
              key={r.value}
              type="button"
              role="radio"
              aria-checked={picked}
              onClick={() => (tab === 'arsiv' ? setRange(r.value) : setActiveRange(r.value))}
              className={`px-3 py-1.5 rounded-full text-[12px] xl:text-[13px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3973bf] ${picked ? 'bg-[#2b2b2b] text-white' : 'bg-white text-[#2b2b2b] border border-gray-200 hover:border-[#3973bf]'}`}
            >{r.label}</button>
          );
        })}
      </div>

      {/* Summary strip */}
      {!busy && shown.length > 0 && (
        <div className="flex gap-3 flex-wrap mb-4">
          {tab === 'aktif' ? (
            <>
              <Stat value={activeShown.length} label="Aktif hedef" />
              <Stat value={behind} label="Geride kalan" tone={behind > 0 ? '#b42318' : '#2b2b2b'} />
              <Stat value={<>{sum(activeShown, 'achieved_value')}<span className="text-[14px] font-semibold text-gray-400"> / {sum(activeShown, 'target_value')}</span></>} label="Çözülen / hedeflenen soru" tone="#3973bf" />
              <Stat value={accuracy(activeShown)} label="Doğruluk" />
            </>
          ) : (
            <>
              <Stat value={rate == null ? '—' : `%${rate}`} label="Hedef tutturma oranı" tone="#3973bf" />
              <Stat value={done} label="Tamamlanan" tone="#2fae4e" />
              <Stat value={missed} label="Tamamlanmayan" tone={missed > 0 ? '#e05252' : '#2b2b2b'} />
              <Stat value={accuracy(archive.items)} label="Doğruluk" />
            </>
          )}
        </div>
      )}

      {busy ? (
        <div className="flex justify-center py-10"><div className="w-7 h-7 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : shown.length === 0 ? (
        <p className="text-center text-gray-400 py-10">{tab === 'arsiv' ? GOAL_ARCHIVE_EMPTY[range] : GOAL_ACTIVE_EMPTY[activeRange]}</p>
      ) : (
        <div className="space-y-3">
          {tab === 'aktif' && (
            <p className="text-[13px] text-gray-500 flex items-center gap-1.5"><AutoMark /><span>işaretli hedefler, öğrencinin eis ekosis LMS'te çözdüğü sorularla otomatik sayılır. Geride kalanlar en üstte.</span></p>
          )}
          {shown.map(({ g, pace }) => {
            const target = g.target_value ?? 0;
            const achieved = g.achieved_value ?? 0;
            const pct = target > 0 ? Math.round((achieved / target) * 100) : 0;
            const phase = goalPhase(g);
            const { color: c, chip } = GOAL_PHASE_STYLE[phase];
            const author = goalAuthorBadge(g);
            const scope = goalScopeLine(g);
            const time = g.start_time && g.end_time ? fmtTimeRange(g.start_time, g.end_time).replace(' - ', '–') : null;
            const paceStyle = pace.state === 'none' ? null : PACE_STYLE[pace.state];
            const line = paceLine(pace);
            const checked = g.status === 'completed' || !!g.is_completed;
            const locked = tickLocked(g);
            const pending = tickPending(g);
            return (
              <div
                key={String(g.id)}
                data-goal-card={String(g.id)}
                className="relative overflow-hidden rounded-[20px] bg-white pl-5 pr-4 py-4 xl:pl-6 xl:pr-5"
                style={goalSpotlightStyle(spotlight.lit === String(g.id), '0px 1px 4px rgba(0,0,0,0.08)')}
              >
                <span aria-hidden className="absolute left-0 inset-y-0 w-[5px]" style={{ backgroundColor: c }} />
                <div className="flex items-start gap-2">
                  <p className="flex-1 min-w-0 text-[16px] xl:text-[18px] font-bold text-[#2b2b2b] leading-snug" style={F}>
                    {goalHeadline(g, 'Genel')}
                    {goalCountsLms(g) && <span className="inline-block align-[-1px] ml-1.5"><AutoMark /></span>}
                  </p>
                  <span className="flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full mt-0.5" style={{ backgroundColor: author.bg, color: author.fg, ...F }}>{author.text}</span>
                  <button
                    onClick={() => onDelete(String(g.id))}
                    aria-label="Hedefi sil"
                    className="flex-shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-[18px] text-gray-400 hover:text-red-600 leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#3973bf]"
                  >×</button>
                </div>
                {scope && <p className="text-[13px] font-semibold text-[#4b5563] mt-0.5" style={F}>{scope}</p>}

                <div className="flex items-center gap-2 mt-0.5 flex-wrap" style={F}>
                  <span className="text-[13px] font-semibold text-[#2b2b2b]">{PERIOD_LABEL[g.period_type ?? 'daily'] ?? 'Hedef'}</span>
                  <span className="text-[13px] text-gray-500">{goalPeriodRange(g)}</span>
                  {time && <span className="text-[13px] text-gray-500 tabular-nums">{time}</span>}
                  <span className="ml-auto flex items-center gap-2">
                    {paceStyle && (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: paceStyle.bg, color: paceStyle.color }}>{paceStyle.label}</span>
                    )}
                    {chip && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: c }}>{chip}</span>}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline gap-1.5" style={F}>
                  <span className="text-[24px] xl:text-[28px] font-bold leading-none tabular-nums" style={{ color: c }}>{achieved}</span>
                  <span className="text-[15px] font-semibold text-gray-500 tabular-nums">/ {target} soru</span>
                  <span className="text-[13px] font-semibold text-gray-400 tabular-nums">%{Math.min(pct, 999)}</span>
                </div>
                <div className="mt-2 h-[8px] bg-[#e8eaee] rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(pct, 100)}>
                  <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: c }} />
                </div>
                {line && <p className="mt-2 text-[13px] font-semibold" style={{ ...F, color: paceStyle?.color }}>{line}</p>}

                {goalHasStats(g) && (
                  <p className="mt-2 text-[13px] text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 tabular-nums" style={F}>
                    <span><b className="font-bold text-[#2fae4e]">{g.correct_count ?? 0}</b> doğru</span>
                    <span><b className="font-bold text-[#e05252]">{g.wrong_count ?? 0}</b> yanlış</span>
                    <span><b className="font-bold text-[#2b2b2b]">%{g.accuracy_rate ?? 0}</b> başarı</span>
                    {goalCountsLms(g) && (g.manual_solved ?? 0) > 0 && <span><b className="font-bold text-[#2b2b2b]">+{g.manual_solved}</b> dışarıdan</span>}
                  </p>
                )}

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => onTick(g)}
                    disabled={pending || locked}
                    aria-pressed={checked}
                    title={locked ? "eis ekosis LMS'te çözülen sorularla tamamlandı; işaret kaldırılamaz" : checked ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}
                    className="inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 h-[30px] text-[12px] font-bold disabled:opacity-60"
                    style={{
                      ...F,
                      background: checked ? '#11D600' : '#ffffff',
                      color: checked ? '#ffffff' : '#3973bf',
                      border: `2px solid ${checked ? '#0DA600' : '#3973bf'}`,
                      cursor: pending ? 'wait' : locked ? 'default' : 'pointer',
                      ...(locked ? { opacity: 1 } : {}),
                    }}
                  >
                    <span className="w-[20px] h-[20px] rounded-full flex items-center justify-center" style={{ background: checked ? 'rgba(255,255,255,0.25)' : '#3973bf' }}>
                      {checked
                        ? <svg width="10" height="8" viewBox="0 0 10 8" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l3 3 5-6" /></svg>
                        : <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><path d="M5 1v8M1 5h8" /></svg>}
                    </span>
                    {checked ? 'Tamamlandı' : 'İşaretle'}
                  </button>
                </div>
              </div>
            );
          })}
          {tab === 'arsiv' && archive.hasMore && (
            <div className="flex justify-center pt-1">
              <button
                onClick={() => loadArchive(range, archive.page + 1)}
                disabled={archive.loading}
                className="px-5 py-2 rounded-[10px] bg-white border border-gray-200 text-[13px] font-semibold text-[#2b2b2b] hover:border-[#3973bf] disabled:opacity-60"
              >
                {archive.loading ? 'Yükleniyor…' : 'Daha fazla göster'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentGoalList;
